import FilterListIcon from '@mui/icons-material/FilterList';
import { Box } from '@mui/material';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DEPT_ADMIN, DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../constants/users';
import { selectAdminType, selectIsAdminTypeUser } from '../../store/selectors/auth';
import { IDepartment } from '../../types/organizations';
import { IFilterItem, IFiltersRef } from '../../types/tables';
import { hasPermission } from '../../utils/users';
import { ClearFiltersButton, DepartmentFilter, FilterSelect } from '../_mui';

interface IProps {
  renderSearchField: React.JSX.Element | null;
  onSearchReset: () => void;
  query?: string;
  selectedDepartments: string[];
  onDepartmentsChange: (departments: string[]) => void;
  filterConfig: IFilterItem[];
  departments: IDepartment[];
  isFiltersEnabled?: boolean;
  actionButton?: React.ReactNode;
}

export const ClaimLinkTemplateFilters = forwardRef<IFiltersRef, IProps>(
  (
    {
      renderSearchField,
      onSearchReset,
      query,
      selectedDepartments,
      onDepartmentsChange,
      filterConfig,
      departments,
      isFiltersEnabled = true,
      actionButton,
    }: IProps,
    filtersRef,
  ) => {
    const adminType = useSelector(selectAdminType);
    const isAdminTypeUser = useSelector(selectIsAdminTypeUser);

    const isDeptFilterShown = useMemo(
      () => isAdminTypeUser && hasPermission([DEPT_ADMIN, ORG_ADMIN, SUPER_ADMIN, DISTRIBUTOR], adminType),
      [isAdminTypeUser, adminType],
    );

    const isAnyFilterActive = useMemo(() => {
      const hasActiveStatusFilter = isFiltersEnabled
        ? filterConfig.some((filter) => filter.value && filter?.value?.length > 0)
        : false;

      return hasActiveStatusFilter || (query && query?.length > 0) || selectedDepartments.length > 0;
    }, [filterConfig, query, selectedDepartments, isFiltersEnabled]);

    const handleClearFilter = useCallback((onChange: (value: string | string[]) => void) => {
      onChange('');
    }, []);

    const handleClearAllFilters = useCallback(() => {
      filterConfig.forEach((filter) => filter.onChange(''));
      onDepartmentsChange([]);

      if (onSearchReset) {
        onSearchReset();
      }
    }, [filterConfig, onDepartmentsChange, onSearchReset]);

    useImperativeHandle(
      filtersRef,
      () => ({
        resetFilters: handleClearAllFilters,
      }),
      [handleClearAllFilters],
    );

    const singleRowStyles = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      flexWrap: 'wrap',
      gap: { xs: 2, sm: 3 },
    };

    const leftSectionStyles = {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      flexWrap: 'wrap',
      gap: { xs: 1.5, sm: 3 },
      minWidth: 0,
    };

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={singleRowStyles}>
          <Box sx={leftSectionStyles}>
            {renderSearchField}
            <>
              <FilterListIcon color="disabled" />
              {filterConfig.map(
                ({
                  tabId,
                  label,
                  value,
                  onChange,
                  options,
                  isMultiple = false,
                  isVisible = true,
                  limit,
                  onLimitReached,
                  enableSearch,
                  dataAction,
                }) =>
                  isVisible ? (
                    <FilterSelect
                      limit={limit}
                      onLimitReached={onLimitReached}
                      isMultiple={isMultiple}
                      key={tabId}
                      label={label}
                      value={value}
                      onChange={onChange}
                      options={options}
                      onClear={handleClearFilter}
                      disabled={!isFiltersEnabled}
                      enableSearch={enableSearch}
                      dataAction={dataAction}
                    />
                  ) : null,
              )}
              {isDeptFilterShown && (
                <DepartmentFilter
                  value={selectedDepartments}
                  onChange={onDepartmentsChange}
                  departments={departments}
                  dataAction="template_department_filter"
                />
              )}
              {isAnyFilterActive && <ClearFiltersButton onClick={handleClearAllFilters} />}
            </>
          </Box>
          {actionButton}
        </Box>
      </Box>
    );
  },
);
